import { AlertTriangle, Clock, CalendarX, TrendingUp } from "lucide-react";
import { computeRate, money, n, round2 } from "./rateMath";
import { fmtDate } from "./trendMath";
import { Section } from "./Section";

const Item = ({ icon: Icon, tone = "gray", children, testId }) => {
  const tones = { gray: "bg-gray-50 text-gray-700", amber: "bg-amber-50 text-amber-900", red: "bg-red-50 text-red-800" };
  return (
    <div className={`flex gap-2.5 rounded-md px-3 py-2.5 text-xs leading-relaxed ${tones[tone]}`} data-testid={testId}>
      <Icon className="w-4 h-4 shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );
};

export const HiddenCostPanel = ({ inputs, result, overheadBaseline, currentRate }) => {
  const hours = n(inputs.hours_per_week);
  const billable = round2(result.weeklyBillable);
  const gap = round2(hours - billable);
  const fullYear = computeRate({ ...inputs, downtime_weeks: 0 }).trueHourly;
  const monthlyNow = result.annualOverhead / 12;
  const creep = overheadBaseline ? monthlyNow - overheadBaseline.monthly : 0;
  const below = currentRate !== null && result.trueHourly > currentRate ? result.trueHourly - currentRate : 0;
  return (
    <Section letter="C" title="Hidden cost / margin leak" description="Is your baseline rate still correct? (The job-level margin alert checks individual quotes against that rate.)" testId="rate-calc-hidden-costs">
      <div className="space-y-2">
        <Item icon={Clock} testId="leak-nonbillable">
          Your shop operates {hours} hrs/week but only {n(inputs.utilization_pct)}% ({billable} hrs) goes to paid job work → {gap} hrs/week isn't recovered in pricing.
        </Item>
        <Item icon={CalendarX} testId="leak-downtime">
          {n(inputs.downtime_weeks)} weeks/year of downtime means your fixed costs are being spread across fewer billable hours than you may assume
          {n(inputs.downtime_weeks) > 0 && result.trueHourly > 0 && (
            <> — across a full 52 weeks your cost would be {money(fullYear)}/hr, but over {result.availableWeeks} working weeks it's {money(result.trueHourly)}/hr</>
          )}.
        </Item>
        {creep > 0.005 && (
          <Item icon={TrendingUp} tone="amber" testId="leak-overhead-creep">
            Overhead has increased {money(creep)}/month since you last reviewed this ({fmtDate(overheadBaseline.date)}), has your Labour Rate been updated to match?
          </Item>
        )}
        {below > 0.005 && (
          <Item icon={AlertTriangle} tone="red" testId="leak-rate-drift">
            Your current rate is {money(below)} below break-even ({money(currentRate)}/hr vs {money(result.trueHourly)}/hr).
          </Item>
        )}
      </div>
    </Section>
  );
};

const ASSUMPTIONS = [
  "Assumes staff are consistently productive during their billable hours.",
  "Operating expenses are assumed relatively stable month to month.",
  "This calculation covers labour and overhead only, excludes materials, supplier costs, and job-specific pricing.",
  "Wages are entered per person, but billable hours come from shop opening hours, not each person's hours.",
];

export const AssumptionsPanel = () => (
  <Section letter="D" title="Assumptions" testId="rate-calc-assumptions">
    <ul className="mt-2 space-y-1.5 text-xs text-gray-600 list-disc pl-4">
      {ASSUMPTIONS.map((a) => <li key={a}>{a}</li>)}
    </ul>
  </Section>
);
